import { LitElement, html, css, nothing } from 'lit';
import { property, state, customElement } from 'lit/decorators.js';
import { SvgIcons } from '../../voyage-svg-icons/src/voyage-svg-icons';
import { HelpCenterCard, HelpCenterCardId, HelpCenterEventNames } from './types';
import '@maersk-global/mds-components-core/mc-button';

const HELP_CENTER_CARDS: Array<HelpCenterCard> = [
    {
        id: HelpCenterCardId.GuidesAndTutorials,
        title: 'Guide & Tutorials',
        description: 'Access all user onboarding, video tutorials here.',
        actionLabel: 'View',
    },
    {
        id: HelpCenterCardId.PopularFaqs,
        title: 'Popular FAQs',
        description: 'Find popular questions and answers here.',
        actionLabel: 'View',
    },
    {
        id: HelpCenterCardId.RaiseACase,
        title: 'Raise a case',
        description: 'Here, you can report issues related to this application.',
        actionLabel: 'Raise Now',
    },
    {
        id: HelpCenterCardId.LatestUpdates,
        title: 'Latest Updates Guide',
        description: 'Get onboarding guides and tutorials for new features.',
        actionLabel: 'Request Now',
        badge: 4,
    },
];

const FAQ_ITEMS = [
    { q: 'What happens if the input data is incorrect?', a: 'If incorrect data is entered, calculated results may be inaccurate. You can edit or correct inputs at any time through the application. Once updated, the system will automatically recalculate all dependent values to reflect the latest information, ensuring consistency and accuracy across the platform without requiring any manual intervention from the user. This helps maintain reliable outputs for decision-making purposes.' },
];

// Base directory (served statically) that holds the guide PDFs.
const isVesselOnCloud = (<any>window)["appenv"].vesselOnCloud;
const GUIDES_DIR = isVesselOnCloud ? 'assets/helpCenter/GuidesCloud/'  : 'assets/helpCenter/GuidesPremium/';

interface TutorialCard {
    title: string;
    description: string;
    action: string;
    file: string;
    isNew?: boolean;
}

// Titles and descriptions are derived from the PDF file names in `assets/helpCenter/GuidesPremium`.
const TUTORIAL_CARDS_PREMIUM: Array<TutorialCard> = [
    { title: 'Polar Chart', description: 'Understand and use the Polar Chart.', action: 'View', file: 'Polar Chart - 20260925.pdf', isNew: true },
    { title: 'StarConnect Voyage ETA Integration', description: 'How voyage ETA data is communicated and integrated through StarConnect.', action: 'View', file: 'StarConnect Voyage ETA Communication Integration - 2026-03.pdf', isNew: true },
    { title: 'Export & Import Text to Passage Plan', description: 'Steps to export and import text-based passage plans.', action: 'View', file: 'VP_QG_Export-Import_Text_to_Passage_Plan_2026_01.pdf', isNew: true },
    { title: 'Main Screen & Functions', description: 'An overview of the main screen and core functions of the Voyage Planner.', action: 'View', file: 'VP_QG_MainScreenAndFunctions_v1.2.pdf' },
    { title: 'Making a New Route (2024)', description: 'Updated step-by-step guide to creating a new route.', action: 'View', file: 'VP_QG_Making new route_2024_03.pdf' },
    { title: 'Making a New Route (2022)', description: 'Step-by-step guide to creating a new route.', action: 'View', file: 'VP_QG_Making new route_2022_10.pdf' },
    { title: 'Route Editor', description: 'Learn how to use the Route Editor to modify routes.', action: 'View', file: 'VP_QG_Route editor_2022_10.pdf' },
    { title: 'Editing Departure & Arrival Time', description: 'How to edit departure and arrival times for your voyage.', action: 'View', file: 'VP_QG_Editing Departure and Arrival time_v1.2.pdf' },
    { title: 'Passage Plan Waypoint Numbering', description: 'Configure whether passage plan waypoint numbering starts at 0 or 1.', action: 'View', file: 'VP_QG_Passage plan WP number start 0 or 1_2022_10.pdf' },
    { title: 'Sailing Directions', description: 'Using sailing directions within the Voyage Planner.', action: 'View', file: 'VP_QG_SailingDirections_2022_10.pdf' },
    { title: 'Weather Tab', description: 'Overview of the Weather tab and available weather layers.', action: 'View', file: 'VP_QG_Weather tab_2022_10.pdf' },
    { title: 'Weather Limits', description: 'Configure and understand weather limits for a safe passage.', action: 'View', file: 'VP_QG_WeatherLimits_2022_10.pdf' },
    { title: 'SPOS8 Integration', description: 'How SPOS8 weather routing integrates with the Voyage Planner.', action: 'View', file: 'VP_QG_SPOS8 Intergration_2022_10.pdf' },
    { title: 'Tide', description: 'Understand tide information and how it affects your passage.', action: 'View', file: 'VP_QG_Tide_2022_10.pdf' },
    { title: 'Uncertainty Band', description: 'Learn about the uncertainty band and its impact on planning.', action: 'View', file: 'VP_QG_Uncertanty Band_2022_10.pdf' },
    { title: 'Performance Tab', description: 'Overview of the Performance tab and its metrics.', action: 'View', file: 'VP_QG_Performance tab_v1.2.pdf' },
    { title: 'Whale Zones', description: 'Information on whale protection zones and how they are displayed.', action: 'View', file: 'VP_QG_Whale_zones_2022_10.pdf' },
    { title: 'Drifting at Sea', description: 'Quick guide on handling drifting-at-sea scenarios in the Voyage Planner.', action: 'View', file: 'VP_QG_Driftig at sea_2022_10.pdf' },
    { title: 'GPS Error', description: 'Guidance on identifying and resolving GPS errors.', action: 'View', file: 'VP_QG_GPS Error_2022_05.pdf' },
    { title: 'Error Handling', description: 'Understand common errors and how to resolve them in the Voyage Planner.', action: 'View', file: 'VP_QG_Error handling_2022_10.pdf' },
    { title: 'NumPad Enablement', description: 'How to enable and use the on-screen number pad.', action: 'View', file: 'VP_QG_NumPad Enablement_2022_10.pdf' },
    { title: 'Change Time Format (US to EUR)', description: 'How to switch the time format from US to European.', action: 'View', file: 'VP_QG_change time from US to EUR_2022_10.pdf' },
    { title:'StarConnect Voyage - Reviewing Route Warnings', description: 'Guide on how to review route warnings in StarConnect Voyage.', action: 'View', file: 'StarConnect Voyage - Reviewing Route Warnings.pdf', isNew: true },
];

// One entry per document actually present in `assets/helpCenter/GuidesCloud`, since
// `GUIDES_DIR` resolves to that folder on cloud — a Premium-only file listed here
// would 404 when opened.
const TUTORIAL_CARDS_CLOUD: Array<TutorialCard> = [
    { title: 'Polar Chart', description: 'Understand and use the Polar Chart.', action: 'View', file: 'Polar Chart - 20260925.pdf', isNew: true },
    { title: 'StarConnect Voyage ETA Integration', description: 'How voyage ETA data is communicated and integrated through StarConnect.', action: 'View', file: 'StarConnect Voyage ETA Communication Integration - 2026-03.pdf', isNew: true },
    { title: 'Export & Import Text to Passage Plan', description: 'Steps to export and import text-based passage plans.', action: 'View', file: 'VP_QG_Export-Import_Text_to_Passage_Plan_2026_01.pdf', isNew: true },
    { title: 'System Overview', description: 'An overview of the Voyage system and its main components.', action: 'View', file: 'System_overview_2025-09.pdf', isNew: true },
    { title: 'Voyage Planning', description: 'Learn how to plan and manage a voyage from start to finish.', action: 'View', file: 'Voyage_Planning_2025_08.pdf' },
    { title: 'Offline & Online Mode', description: 'Quick guide to working in offline and online mode.', action: 'View', file: 'Quick Guide - Offline & Online Mode.pdf', isNew: true },
    { title:'StarConnect Voyage - Reviewing Route Warnings', description: 'Guide on how to review route warnings in StarConnect Voyage.', action: 'View', file: 'StarConnect Voyage - Reviewing Route Warnings.pdf', isNew: true },
];

// Active guide list — Cloud documents when logged in on cloud, otherwise the Premium PDFs.
const TUTORIAL_CARDS: Array<TutorialCard> = isVesselOnCloud ? TUTORIAL_CARDS_CLOUD : TUTORIAL_CARDS_PREMIUM;

interface SearchSuggestion {
    label: string;
    category: string;
    view: 'guides' | 'faqs' | 'latestUpdates';
}

const SEARCH_INDEX: SearchSuggestion[] = [
    ...TUTORIAL_CARDS.map(c => ({ label: c.title, category: 'Guide & Tutorials', view: 'guides' as const })),
    ...FAQ_ITEMS.map(f => ({ label: f.q, category: 'Popular FAQs', view: 'faqs' as const })),
    ...TUTORIAL_CARDS.map(c => ({ label: c.title, category: 'Latest Updates', view: 'latestUpdates' as const })),
];

@customElement('voyage-help-center')
export class VoyageHelpCenter extends LitElement {
    @property({ type: Boolean, reflect: true }) shown: boolean = false;
    @property({ type: Array }) cards: Array<HelpCenterCard> = HELP_CENTER_CARDS;
    @state() private _activeView: 'home' | 'guides' | 'faqs' | 'latestUpdates' | 'raiseACase' = 'home';
    @state() private _openFaqIndex: number | null = 0;
    @state() private _searchQuery: string = '';
    @state() private _suggestions: SearchSuggestion[] = [];
    @state() private _showSuggestions: boolean = false;
    @state() private _pageSearchQuery: string = '';
    private _debounceTimer: ReturnType<typeof setTimeout> | null = null;
    private _pageDebounceTimer: ReturnType<typeof setTimeout> | null = null;

    static get styles() {
        return [
            css`
            :host {
                display: none;
            }
            :host([shown]) {
                display: block;
                position: fixed;
                left: 0;
                right: 0;
                top: 49px;
                bottom: 0;
                z-index: 5;
                overflow-y: auto;
                background-color: var(--neutrals-grey-10, white);
                font-family: MaerskText;
                color: var(--neutrals-grey-02, black);
            }

            .breadcrumb {
                display: flex;
                align-items: center;
                column-gap: 8px;
                padding: 16px 24px;
                font-size: 14px;
                line-height: 20px;
            }
            .breadcrumb a {
                color: var(--neutrals-grey-02, black);
                text-decoration: none;
                cursor: pointer;
            }
            .breadcrumb a:hover {
                text-decoration: underline;
            }
            .breadcrumb .separator {
                color: var(--neutrals-grey-05, grey);
            }
            .breadcrumb .current {
                color: var(--neutrals-grey-04, grey);
            }

            .hero {
                position: relative;
                display: flex;
                flex-direction: column;
                align-items: center;
                justify-content: center;
                text-align: center;
                min-height: 460px;
                padding: 48px 24px 80px;
                box-sizing: border-box;
                color: var(--neutrals-grey-10, white);
                background-color: var(--primary-blue-01, #00233D);
                background-image:
                    linear-gradient(180deg, rgba(0, 35, 61, 0.30) 0%, rgba(0, 35, 61, 0.45) 100%),
                    url('./assets/images/helpCenter-bg.png');
                background-size: cover;
                background-position: center;
                background-repeat: no-repeat;
                border-bottom: 4px solid var(--primary-blue-07, #42AFD6);
            }
            .hero-title {
                font-family: MaerskHeadline, "Maersk Text", sans-serif;
                font-size: 36px;
                font-weight: bold;
                line-height: 48px;
                margin: 0 0 16px;
                color: #ffffff;
            }
            .hero-subtitle {
                max-width: 760px;
                font-size: 16px;
                line-height: 24px;
                margin: 0;
                color: #ffffff;
                text-align: center;
            }

            .search-card {
                position: absolute;
                z-index: 1;
                left: 50%;
                bottom: -40px;
                transform: translateX(-50%);
                width: calc(100% - 48px);
                max-width: 780px;
                padding: 32px 40px;
                box-sizing: border-box;
                background-color: var(--neutrals-grey-10, white);
                box-shadow: 0 6px 16px rgba(var(--neutrals-grey-01-rgb, 20, 20, 20), 0.12);
                border-radius: 4px;
            }
            .search-input-container {
                display: flex;
                align-items: stretch;
                column-gap: 0;
            }
            .search-field {
                position: relative;
                flex: 1 1 auto;
            }
            .clear-btn {
                position: absolute;
                right: 10px;
                top: 50%;
                transform: translateY(-50%);
                display: flex;
                align-items: center;
                justify-content: center;
                width: 18px;
                height: 18px;
                border-radius: 50%;
                border: none;
                background: var(--neutrals-grey-05, #aaa);
                color: white;
                font-size: 12px;
                line-height: 1;
                cursor: pointer;
                padding: 0;
            }
            .clear-btn:hover { background: var(--neutrals-grey-03, #555); }
            input {
                width: 100%;
                height: 52px;
                box-sizing: border-box;
                padding: 0 36px 0 16px;
                border: 1px solid var(--neutrals-grey-06, grey);
                border-right: none;
                border-radius: 4px 0 0 4px;
                font-family: MaerskText;
                font-size: 14px;
                line-height: 20px;
                color: var(--neutrals-grey-02, black);
            }
            input:focus,
            input:focus-visible {
                outline: none;
                border-color: var(--neutrals-grey-06, grey);
            }
            input::placeholder {
                color: var(--neutrals-grey-05, grey);
            }
            .search-button {
                display: flex;
                flex: 0 0 auto;
                align-items: center;
                justify-content: center;
                width: 52px;
                height: 52px;
                padding: 0;
                border: none;
                border-radius: 0 4px 4px 0;
                cursor: pointer;
                background-color: var(--neutrals-grey-01, black);
            }
            .search-button svg {
                width: 20px;
                height: 20px;
            }
            .search-button svg path {
                fill: var(--neutrals-grey-10, white);
            }

            .cards {
                display: grid;
                grid-template-columns: repeat(4, 1fr);
                gap: 24px;
                max-width: 1280px;
                margin: 0 auto;
                padding: 96px 24px 48px;
                box-sizing: border-box;
            }
            .card {
                display: flex;
                flex-direction: column;
                padding: 24px;
                box-sizing: border-box;
                border: 1px solid var(--neutrals-grey-06, grey);
                border-radius: 4px;
                background-color: var(--neutrals-grey-10, white);
            }
            .card.disabled {
                opacity: 0.5;
                pointer-events: none;
                background-color: var(--neutrals-grey-09, #f5f5f5);
            }
            .card.disabled .card-title,
            .card.disabled .card-description {
                color: var(--neutrals-grey-05, grey);
            }
            .card-title {
                display: flex;
                align-items: center;
                column-gap: 8px;
                font-family: MaerskHeadline;
                font-size: 18px;
                line-height: 24px;
                font-weight: bold;
                color: var(--neutrals-grey-01, black);
                margin: 0 0 12px;
            }
            .card-badge {
                display: inline-flex;
                align-items: center;
                justify-content: center;
                min-width: 20px;
                height: 20px;
                padding: 0 6px;
                box-sizing: border-box;
                border-radius: 10px;
                background-color: var(--voyage-good-green, green);
                color: var(--neutrals-grey-10, white);
                font-family: MaerskText;
                font-size: 12px;
                line-height: 20px;
            }
            .card-description {
                flex: 1 1 auto;
                font-size: 14px;
                line-height: 20px;
                color: var(--neutrals-grey-03, grey);
                margin: 0 0 24px;
            }

            @media (max-width: 1024px) {
                .cards {
                    grid-template-columns: repeat(2, 1fr);
                }
            }

            /* Guides view */
            .guides-page {
                padding: 24px 32px;
                font-family: MaerskText;
            }
            .guides-breadcrumb {
                display: flex;
                column-gap: 6px;
                font-size: 13px;
                margin-bottom: 16px;
                flex-wrap: wrap;
            }
            .guides-breadcrumb a {
                color: var(--primary-blue-03, #005487);
                text-decoration: none;
                cursor: pointer;
            }
            .guides-breadcrumb a:hover { text-decoration: underline; }
            .guides-breadcrumb .sep { color: var(--neutrals-grey-05, grey); }
            .guides-breadcrumb .cur { color: var(--neutrals-grey-04, grey); }
            .guides-header {
                margin-bottom: 20px;
            }
            .guides-header h1 {
                font-family: MaerskHeadline;
                font-size: 28px;
                font-weight: bold;
                margin: 0 0 4px;
                color: var(--neutrals-grey-01, black);
            }
            .guides-header p {
                font-size: 14px;
                color: var(--neutrals-grey-03, grey);
                margin: 0 0 20px;
            }
            .guides-search-row {
                display: flex;
                align-items: stretch;
                max-width: 400px;
                margin-bottom: 28px;
            }
            .guides-search-row .search-field {
                position: relative;
                flex: 1;
            }
            .guides-search-row input {
                width: 100%;
                box-sizing: border-box;
                height: 40px;
                border: 1px solid var(--neutrals-grey-06, grey);
                border-right: none;
                border-radius: 4px 0 0 4px;
                padding: 0 32px 0 12px;
                font-family: MaerskText;
                font-size: 14px;
            }
            .guides-search-row input:focus, .guides-search-row input:focus-visible { outline: none; }
            .guides-search-btn {
                display: flex;
                align-items: center;
                justify-content: center;
                width: 40px;
                height: 40px;
                background: var(--neutrals-grey-01, black);
                border: none;
                border-radius: 0 4px 4px 0;
                cursor: pointer;
            }
            .guides-search-btn svg { width: 16px; height: 16px; }
            .guides-search-btn svg path { fill: white; }
            .guides-grid {
                display: grid;
                grid-template-columns: repeat(4, 1fr);
                gap: 16px;
                border: 1px solid var(--neutrals-grey-06, grey);
                border-radius: 4px;
                padding: 24px;
            }
            .t-card {
                position: relative;
                display: flex;
                flex-direction: column;
                border: 1px solid var(--neutrals-grey-06, grey);
                border-radius: 8px;
                padding: 16px;
            }
            .t-card-badge {
                position: absolute;
                top: -10px;
                right: -30px;
                transform: translateX(-50%);
                background: var(--voyage-olive-green, #dcf6d9);
                font-size: 11px;
                padding: 2px 10px;
                border-radius: 10px;
            }
            .t-card h3 {
                font-size: 15px;
                font-weight: bold;
                margin: 4px 0 8px;
                color: var(--neutrals-grey-01, black);
            }
            .t-card p {
                font-size: 13px;
                color: var(--neutrals-grey-03, grey);
                flex: 1;
                margin: 0 0 16px;
            }
            @media (max-width: 1024px) {
                .guides-grid { grid-template-columns: repeat(2, 1fr); }
            }

            /* Search suggestions */
            .search-field {
                position: relative;
            }
            .suggestions {
                position: absolute;
                top: 100%;
                left: 0;
                right: 0;
                background: white;
                border: 1px solid var(--neutrals-grey-06, #e0e0e0);
                border-top: none;
                border-radius: 0 0 4px 4px;
                box-shadow: 0 4px 12px rgba(0,0,0,0.1);
                z-index: 10;
                max-height: 320px;
                overflow-y: auto;
            }
            .suggestion-group-label {
                padding: 8px 16px 4px;
                font-size: 11px;
                font-weight: bold;
                letter-spacing: 0.5px;
                text-transform: uppercase;
                color: var(--neutrals-grey-04, grey);
                background: var(--neutrals-grey-09, #f5f5f5);
            }
            .suggestion-item {
                display: flex;
                align-items: center;
                column-gap: 10px;
                padding: 10px 16px;
                font-size: 14px;
                color: var(--neutrals-grey-02, black);
                cursor: pointer;
                border-bottom: 1px solid var(--neutrals-grey-08, #f0f0f0);
            }
            .suggestion-item:last-child { border-bottom: none; }
            .suggestion-item:hover { background: var(--neutrals-grey-09, #f5f5f5); }
            .suggestion-item svg { width: 14px; height: 14px; flex-shrink: 0; color: var(--neutrals-grey-04, grey); }
            .suggestion-highlight { font-weight: bold; color: var(--primary-blue-03, #005487); }
            .no-results {
                padding: 16px;
                font-size: 14px;
                color: var(--neutrals-grey-04, grey);
                text-align: center;
            }

            /* FAQs view */
            .faq-item {
                border: 1px solid var(--neutrals-grey-06, #e0e0e0);
                border-radius: 4px;
                margin-bottom: 8px;
                overflow: hidden;
            }
            .faq-question {
                display: flex;
                justify-content: space-between;
                align-items: center;
                padding: 16px 20px;
                cursor: pointer;
                background: var(--neutrals-grey-10, white);
                font-family: MaerskText;
                font-size: 15px;
                font-weight: bold;
                color: var(--neutrals-grey-01, black);
                user-select: none;
            }
            .faq-question svg {
                width: 16px;
                height: 16px;
                flex-shrink: 0;
                color: var(--neutrals-grey-03, grey);
            }
            .faq-answer {
                padding: 0 20px 16px;
                font-size: 14px;
                line-height: 20px;
                color: var(--neutrals-grey-03, grey);
                font-family: MaerskText;
            }
            `
        ];
    }

    /**
     * Go back to the Help & Support Center home view and clear any search state.
     * Public so the host can reset the panel when "Help Center" is picked from the
     * main menu while the panel is already open on a sub-page.
     */
    resetToHome() {
        this._activeView = 'home';
        this._searchQuery = '';
        this._suggestions = [];
        this._showSuggestions = false;
        this._pageSearchQuery = '';
        this._openFaqIndex = 0;
        if (this._debounceTimer) { clearTimeout(this._debounceTimer); this._debounceTimer = null; }
        if (this._pageDebounceTimer) { clearTimeout(this._pageDebounceTimer); this._pageDebounceTimer = null; }
    }

    render() {
        if (this._activeView === 'guides') return this._renderGuidesPage();
        if (this._activeView === 'faqs') return this._renderFaqsPage();
        if (this._activeView === 'latestUpdates') return this._renderLatestUpdatesPage();
        //if (this._activeView === 'raiseACase') return this._renderRaiseACasePage();
        return this._renderHomePage();
    }

    private _renderHomePage() {
        return html`
            <nav class="breadcrumb">
                <a @click=${this._onHomeClick}>Home</a>
                <span class="separator">/</span>
                <span class="current">Help &amp; Support Center</span>
            </nav>

            <section class="hero">
                <h1 class="hero-title">Help &amp; Support Center</h1>
                <p class="hero-subtitle">
                    You can search for specific features or questions, access relevant FAQs, raise support
                    requests, and explore onboarding tutorials to better understand available common features.
                </p>
                <div class="search-card">
                    <div class="search-input-container">
                        <div class="search-field">
                            <input
                                type="text"
                                placeholder="What can we help you with?"
                                .value=${this._searchQuery}
                                @input=${this._onSearchInput}
                                @blur=${() => setTimeout(() => this._showSuggestions = false, 150)}
                                @focus=${() => { if (this._suggestions.length) this._showSuggestions = true; }}
                                autocomplete="off"
                            >
                            ${this._searchQuery ? html`<button class="clear-btn" @click=${() => { this._searchQuery = ''; this._suggestions = []; this._showSuggestions = false; }} aria-label="Clear">&#x2715;</button>` : nothing}
                            ${this._showSuggestions ? this._renderSuggestions() : nothing}
                        </div>
                        <button class="search-button" aria-label="Search">
                            ${SvgIcons.lightSearch}
                        </button>
                    </div>
                </div>
            </section>

            <div class="cards">
                ${this.cards.map((card) => this._renderCard(card))}
            </div>
        `;
    }

    private _renderGuidesPage() {
        const q = this._pageSearchQuery.toLowerCase();
        const filtered = q ? TUTORIAL_CARDS.filter(c => c.title.toLowerCase().includes(q) || c.description.toLowerCase().includes(q)) : TUTORIAL_CARDS;
        return html`
            <div class="guides-page">
                <div class="guides-breadcrumb">
                    <a @click=${this._onHomeClick}>Home</a>
                    <span class="sep">/</span>
                    <a @click=${() => { this._activeView = 'home'; this._pageSearchQuery = ''; }}>Help &amp; Support Center</a>
                    <span class="sep">/</span>
                    <span class="cur">Application Guide</span>
                </div>
                <div class="guides-header">
                    <h1>Guide &amp; Tutorials</h1>
                    <p>Access user onboarding and video tutorials here.</p>
                    <div class="guides-search-row">
                        <div class="search-field">
                            <input type="text" placeholder="Search for topics" .value=${this._pageSearchQuery} @input=${this._onPageSearchInput}>
                            ${this._pageSearchQuery ? html`<button class="clear-btn" @click=${() => this._pageSearchQuery = ''} aria-label="Clear">&#x2715;</button>` : nothing}
                        </div>
                        <button class="guides-search-btn" aria-label="Search">${SvgIcons.lightSearch}</button>
                    </div>
                </div>
                <div class="guides-grid">
                    ${filtered.length ? filtered.map(c => html`
                        <div class="t-card">
                            ${c.isNew ? html`<span class="t-card-badge">New</span>` : nothing}
                            <h3>${c.title}</h3>
                            <p>${c.description}</p>
                            <div>
                                <mc-button appearance="primary" variant="filled" fit="small" @click=${() => this._openGuide(c.file)}>${c.action}</mc-button>
                            </div>
                        </div>
                    `) : html`<p class="no-results">No results found for "${this._pageSearchQuery}"</p>`}
                </div>
            </div>
        `;
    }

    private _openGuide(file: string) {
        // Open the guide in a new browser tab. Passing no window-features string
        // ensures a real tab (a features string makes browsers open a popup window).
        const opened = window.open(encodeURI(`${GUIDES_DIR}${file}`), '_blank');
        if (opened) opened.opener = null;
    }

    private _renderFaqsPage() {
        const q = this._pageSearchQuery.toLowerCase();
        const filtered = q ? FAQ_ITEMS.filter(f => f.q.toLowerCase().includes(q) || f.a.toLowerCase().includes(q)) : FAQ_ITEMS;
        return html`
            <div class="guides-page">
                <div class="guides-breadcrumb">
                    <a @click=${this._onHomeClick}>Home</a>
                    <span class="sep">/</span>
                    <a @click=${() => { this._activeView = 'home'; this._pageSearchQuery = ''; }}>Help &amp; Support Center</a>
                    <span class="sep">/</span>
                    <span class="cur">Popular FAQs</span>
                </div>
                <div class="guides-header">
                    <h1>Popular FAQs</h1>
                    <p>Find popular questions and answers here.</p>
                    <div class="guides-search-row">
                        <div class="search-field">
                            <input type="text" placeholder="Search for topics" .value=${this._pageSearchQuery} @input=${this._onPageSearchInput}>
                            ${this._pageSearchQuery ? html`<button class="clear-btn" @click=${() => this._pageSearchQuery = ''} aria-label="Clear">&#x2715;</button>` : nothing}
                        </div>
                        <button class="guides-search-btn" aria-label="Search">${SvgIcons.lightSearch}</button>
                    </div>
                </div>
                <div>
                    ${filtered.length ? filtered.map((item) => {
                        const i = FAQ_ITEMS.indexOf(item);
                        return html`
                        <div class="faq-item">
                            <div class="faq-question" @click=${() => this._openFaqIndex = this._openFaqIndex === i ? null : i}>
                                <span>${item.q}</span>
                                ${this._openFaqIndex === i ? SvgIcons.lightChevronUp : SvgIcons.lightChevronDown}
                            </div>
                            ${this._openFaqIndex === i && item.a ? html`<div class="faq-answer">${item.a}</div>` : nothing}
                        </div>`;
                    }) : html`<p class="no-results">No results found for "${this._pageSearchQuery}"</p>`}
                </div>
            </div>
        `;
    }

    private _renderLatestUpdatesPage() {
        const q = this._pageSearchQuery.toLowerCase();
        const filtered = q ? TUTORIAL_CARDS.filter(c => c.title.toLowerCase().includes(q) || c.description.toLowerCase().includes(q)) : TUTORIAL_CARDS;
        return html`
            <div class="guides-page">
                <div class="guides-breadcrumb">
                    <a @click=${this._onHomeClick}>Home</a>
                    <span class="sep">/</span>
                    <a @click=${() => { this._activeView = 'home'; this._pageSearchQuery = ''; }}>Help &amp; Support Center</a>
                    <span class="sep">/</span>
                    <span class="cur">Latest Updates Guide</span>
                </div>
                <div class="guides-header">
                    <h1>Latest Updates Guide</h1>
                    <p>Get onboarding guides and tutorials for new features.</p>
                    <div class="guides-search-row">
                        <div class="search-field">
                            <input type="text" placeholder="Search for topics" .value=${this._pageSearchQuery} @input=${this._onPageSearchInput}>
                            ${this._pageSearchQuery ? html`<button class="clear-btn" @click=${() => this._pageSearchQuery = ''} aria-label="Clear">&#x2715;</button>` : nothing}
                        </div>
                        <button class="guides-search-btn" aria-label="Search">${SvgIcons.lightSearch}</button>
                    </div>
                </div>
                <div class="guides-grid">
                    ${filtered.length ? filtered.map(c => html`
                        <div class="t-card">
                            ${c.isNew ? html`<span class="t-card-badge">New</span>` : nothing}
                            <h3>${c.title}</h3>
                            <p>${c.description}</p>
                            <div>
                                <mc-button appearance="primary" variant="filled" fit="small" @click=${() => this._openGuide(c.file)}>${c.action}</mc-button>
                            </div>
                        </div>
                    `) : html`<p class="no-results">No results found for "${this._pageSearchQuery}"</p>`}
                </div>
            </div>
        `;
    }

    private _onPageSearchInput(e: Event) {
        const value = (e.target as HTMLInputElement).value;
        if (this._pageDebounceTimer) clearTimeout(this._pageDebounceTimer);
        this._pageDebounceTimer = setTimeout(() => {
            this._pageSearchQuery = value;
        }, 300);
    }

    private _onSearchInput(e: Event) {
        const value = (e.target as HTMLInputElement).value;
        this._searchQuery = value;
        if (this._debounceTimer) clearTimeout(this._debounceTimer);
        if (!value.trim()) {
            this._suggestions = [];
            this._showSuggestions = false;
            return;
        }
        this._debounceTimer = setTimeout(() => {
            const q = value.toLowerCase();
            this._suggestions = SEARCH_INDEX.filter(item =>
                item.label.toLowerCase().includes(q)
            ).slice(0, 20);
            this._showSuggestions = true;
        }, 300);
    }

    private _renderSuggestions() {
        if (!this._suggestions.length) {
            return html`<div class="suggestions"><div class="no-results">No results found</div></div>`;
        }
        const grouped = this._suggestions.reduce((acc, s) => {
            (acc[s.category] = acc[s.category] || []).push(s);
            return acc;
        }, {} as Record<string, SearchSuggestion[]>);
        return html`
            <div class="suggestions">
                ${Object.entries(grouped).map(([cat, items]) => html`
                    <div class="suggestion-group-label">${cat}</div>
                    ${items.map(item => html`
                        <div class="suggestion-item" @mousedown=${() => this._onSuggestionClick(item)}>
                            ${SvgIcons.lightSearch}
                            <span>${this._highlight(item.label, this._searchQuery)}</span>
                        </div>
                    `)}
                `)}
            </div>
        `;
    }

    private _highlight(text: string, query: string) {
        const idx = text.toLowerCase().indexOf(query.toLowerCase());
        if (idx === -1) return html`${text}`;
        return html`${text.slice(0, idx)}<span class="suggestion-highlight">${text.slice(idx, idx + query.length)}</span>${text.slice(idx + query.length)}`;
    }

    private _onSuggestionClick(item: SearchSuggestion) {
        this._searchQuery = '';
        this._suggestions = [];
        this._showSuggestions = false;
        this._pageSearchQuery = '';
        if (item.view === 'faqs') {
            const idx = FAQ_ITEMS.findIndex(f => f.q === item.label);
            this._openFaqIndex = idx !== -1 ? idx : null;
        }
        this._activeView = item.view;
    }

    private _renderCard(card: HelpCenterCard) {
        const disabled = card.id === HelpCenterCardId.RaiseACase
            || card.id === HelpCenterCardId.LatestUpdates
        const onClick = disabled
            ? undefined
            : card.id === HelpCenterCardId.GuidesAndTutorials
            ? () => this._activeView = 'guides'
            : card.id === HelpCenterCardId.PopularFaqs
            ? () => this._activeView = 'faqs'
            : undefined;
        return html`
            <div class="card ${disabled ? 'disabled' : ''}">
                <h2 class="card-title">
                    ${card.title}
                    ${card.badge ? html`<span class="card-badge">${card.badge}</span>` : nothing}
                </h2>
                <p class="card-description">${card.description}</p>
                <div>
                    <mc-button appearance="primary" variant="filled" fit="small" ?disabled=${disabled} @click=${onClick}>
                        ${card.actionLabel}
                    </mc-button>
                </div>
            </div>
        `;
    }

    private _onHomeClick() {
        this.dispatchEvent(new Event(HelpCenterEventNames.Close));
    }
}
